/**
 * LatencyBandChart — latency percentiles over time as a shaded p50→p95 band
 * with a p99 tail line. Reads a percentile per point; degrades composably: with
 * only p95 it draws a single line, with p50+p95 it fills the band, and p99 adds
 * a dashed tail. The hover tooltip lists whichever percentiles are present.
 */
import { useId, type ReactNode } from "react";
import { scaleLinear } from "@visx/scale";
import { Area, LinePath } from "@visx/shape";
import { LinearGradient } from "@visx/gradient";
import { curveMonotoneX } from "@visx/curve";
import { ChartCanvas } from "./ChartCanvas.js";
import { CHART_COLORS, CHART_HEIGHT, niceTop } from "./tokens.js";

export interface LatencySample {
  readonly p50?: number;
  readonly p95?: number;
  readonly p99?: number;
}

export interface LatencyBandChartProps {
  readonly points: readonly LatencySample[];
  /** Format ms for the tooltip. */
  readonly format: (ms: number) => string;
  readonly height?: number;
}

export function LatencyBandChart({
  points,
  format,
  height = CHART_HEIGHT,
}: LatencyBandChartProps): ReactNode {
  const gradId = useId();
  // Need at least two points carrying p95 to draw a trend.
  const series = points.filter((p): p is LatencySample & { p95: number } => p.p95 != null);
  if (series.length < 2) return null;
  const hasBand = series.every((p) => p.p50 != null);
  const hasTail = series.some((p) => p.p99 != null);
  const top = niceTop(Math.max(...series.map((p) => p.p99 ?? p.p95)));
  return (
    <ChartCanvas
      count={series.length}
      height={height}
      ariaLabel="Latency percentiles trend"
      tooltip={(i) => {
        const p = series[i];
        return [
          p.p50 != null ? `p50 ${format(p.p50)}` : null,
          `p95 ${format(p.p95)}`,
          p.p99 != null ? `p99 ${format(p.p99)}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
      }}
    >
      {({ innerH, x, hover }) => {
        const y = scaleLinear<number>({ domain: [0, top], range: [innerH, 0] });
        return (
          <>
            {hasBand && (
              <>
                <LinearGradient
                  id={gradId}
                  from={CHART_COLORS.band}
                  to={CHART_COLORS.band}
                  fromOpacity={0.3}
                  toOpacity={0.06}
                />
                <Area
                  data={series as (LatencySample & { p95: number })[]}
                  x={(_d, i) => x(i)}
                  y0={(d) => y(d.p50 ?? d.p95)}
                  y1={(d) => y(d.p95)}
                  curve={curveMonotoneX}
                  fill={`url(#${gradId})`}
                />
              </>
            )}
            {hasBand && (
              <LinePath
                data={series as (LatencySample & { p95: number })[]}
                x={(_d, i) => x(i)}
                y={(d) => y(d.p50 ?? d.p95)}
                curve={curveMonotoneX}
                stroke={CHART_COLORS.p50}
                strokeWidth={1}
                strokeOpacity={0.7}
              />
            )}
            <LinePath
              data={series as (LatencySample & { p95: number })[]}
              x={(_d, i) => x(i)}
              y={(d) => y(d.p95)}
              curve={curveMonotoneX}
              stroke={CHART_COLORS.p95}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
            {hasTail && (
              <LinePath
                data={series as (LatencySample & { p95: number })[]}
                x={(_d, i) => x(i)}
                y={(d) => y(d.p99 ?? d.p95)}
                curve={curveMonotoneX}
                stroke={CHART_COLORS.p99}
                strokeWidth={1}
                strokeDasharray="3 2"
                strokeOpacity={0.85}
              />
            )}
            {hover != null && (
              <circle
                cx={x(hover)}
                cy={y(series[hover].p95)}
                r={2.5}
                fill={CHART_COLORS.p95}
                stroke="#0f172a"
                strokeWidth={1}
              />
            )}
          </>
        );
      }}
    </ChartCanvas>
  );
}
